import { useEffect } from 'react'

/**
 * Full-screen document viewer.
 *
 * Documents used to open with `window.open(signedUrl)`. In a browser tab that
 * is fine, but inside the iOS WebView there is no tab bar and no browser
 * chrome, so the PDF took over the screen with no way back short of killing
 * the app. This keeps the document inside the app and puts a Back button above
 * it that is always reachable.
 *
 * PDFs render in an <iframe> and images in an <img>; anything else falls back
 * to an explicit "Open in browser" link rather than a blank frame.
 */
export default function DocumentViewer({ url, name, kind, onClose }) {
  // Hardware/browser back should close the viewer, not leave the app. Pushing
  // a history entry on open means the back gesture pops this instead.
  useEffect(() => {
    window.history.pushState({ viewer: true }, '')
    const onPop = () => onClose()
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [onClose])

  // Escape closes it on desktop.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // The viewer is fixed and full-bleed, so stop the page behind it scrolling.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  function back() {
    // Undo the entry pushed on open so the app's own history stays clean.
    if (window.history.state?.viewer) window.history.back()
    else onClose()
  }

  return (
    <div
      role="dialog"
      aria-label={name || 'Document'}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: '#1C2B2A',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Bar sits above the document and respects the iOS status bar inset. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          paddingTop: 'calc(10px + env(safe-area-inset-top))',
          background: 'var(--teal)',
          color: '#fff',
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={back}
          style={{
            background: 'rgba(255,255,255,0.16)',
            border: 'none',
            color: '#fff',
            fontWeight: 700,
            fontSize: 15,
            padding: '10px 14px',
            borderRadius: 10,
            minHeight: 44,
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <span
          style={{
            fontWeight: 600,
            fontSize: 15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, background: '#1C2B2A', overflow: 'auto' }}>
        {kind === 'image' ? (
          <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
            <img src={url} alt={name} style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }} />
          </div>
        ) : kind === 'pdf' ? (
          <iframe
            src={url}
            title={name || 'Document'}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        ) : (
          <div className="stack center" style={{ padding: 32, color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: 34 }} aria-hidden>📄</div>
            <p style={{ fontSize: 15, lineHeight: 1.6 }}>
              This file type can't be previewed in the app.
            </p>
            <a
              className="btn"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              Open in browser →
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
